/**
 * refreshEvents — 2026-06-07 sitewide-refresh-and-billing-restore.
 *
 * Tiny pub/sub over window CustomEvents to coordinate cross-component
 * refresh after a button-driven refetch (Refresh Broker Data, Run Scan).
 *
 * Listeners subscribe via subscribeRefresh and components that
 * complete a real refetch call emitRefresh after the network call
 * succeeds. The current bus is intentionally local-only; no fake
 * success, no timers.
 */

export type RefreshChannel =
  | "portfolio"        // /api/v1/user/portfolio + perf-history
  | "market-pulse"     // /api/v1/market-pulse/movers
  | "brokerage";       // /api/v1/user/broker-connections + /user/portfolio

const _ev = (ch: RefreshChannel) => `adversyn:refresh:${ch}`;

export function emitRefresh(ch: RefreshChannel, detail?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(_ev(ch), { detail: detail ?? {} }));
  } catch {
    /* ignore */
  }
}

export function subscribeRefresh(
  ch: RefreshChannel,
  cb: (detail: Record<string, unknown>) => void,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (e: Event) => {
    try {
      cb(((e as CustomEvent).detail as Record<string, unknown>) || {});
    } catch {
      /* ignore */
    }
  };
  window.addEventListener(_ev(ch), handler);
  return () => window.removeEventListener(_ev(ch), handler);
}

/**
 * Canonical UI copy for refresh button states. Reuse so every
 * surface speaks the same language.
 */
export const REFRESH_COPY = {
  loading: "Refreshing…",
  successPrefix: "Updated",
  failurePrefix: "Refresh failed",
} as const;
